import { useEffect, useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
  FaExpand,
  FaPlus,
  FaMinus,
  FaExclamationTriangle,
} from 'react-icons/fa';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// The factors (add-ons) the booking already has: /checkin-billing-details/ lists them as an
// "Add-on Factors" line under Room Charges, each with its factor_id and factor_name.
const extractBookedFactors = (billingData) => {
  const items = billingData?.order_details?.['Room Charges'] || [];
  const factorItem = items.find((item) => item.order_type === 'Add-on Factors');
  return (factorItem?.factors || []).filter((f) => f && f.factor_name);
};

const formatStayDate = (value) =>
  value
    ? new Date(value).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : 'N/A';

// Append bedtype factor names to the room type name, e.g. "Suite - Single Queen Bed".
// Works with BOTH shapes:
//   - billing API:              { factor_name, factor_type }
//   - modification options API: { factorname, factor_type }
const getRoomTypeDisplayName = (roomTypeName, factors = []) => {
  const bedFactors = (factors || []).filter((f) => {
    const type = (f?.factor_type || '').toLowerCase();
    const name = f?.factorname || f?.factor_name;
    return type === 'bedtype' && name;
  });
  if (bedFactors.length === 0) return roomTypeName;
  const names = bedFactors.map((f) => f.factorname || f.factor_name);
  return `${roomTypeName} - ${names.join(', ')}`;
};

// Prefer the backend-provided `room_type_with_bed` when present.
// Falls back to `room_type` (raw name) if the backend didn't send it.
const getAssignedRoomDisplayName = (room) => {
  if (!room) return 'Room';
  return room.room_type_with_bed || room.room_type || 'Room';
};

const EnhanceStay = ({ currentStep, setCurrentStep, totalSteps }) => {
  const [modificationOptions, setModificationOptions] = useState(null);
  const [amenities, setAmenities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUpgrade, setSelectedUpgrade] = useState(null);
  const [selectedAmenities, setSelectedAmenities] = useState([]);
  const [amenitiesScrollLeft, setAmenitiesScrollLeft] = useState(0);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [showAmenityModal, setShowAmenityModal] = useState(false);
  const [currentAmenity, setCurrentAmenity] = useState(null);
  const [amenityQuantity, setAmenityQuantity] = useState(1);
  const [numberOfGuests, setNumberOfGuests] = useState(1);
  const [showRoomImagesModal, setShowRoomImagesModal] = useState(false);
  const [currentRoomImages, setCurrentRoomImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [roomAvailability, setRoomAvailability] = useState({});
  const [lengthOfStay, setLengthOfStay] = useState(1);
  const [bookedFactors, setBookedFactors] = useState([]);
  // bedtype factors from the modification options API
  const [appliedBedFactors, setAppliedBedFactors] = useState([]);
  const [roomStatusInfo, setRoomStatusInfo] = useState({});
  const [showUpgradeConfirmation, setShowUpgradeConfirmation] = useState(false);
  const [upgradeConfirmationData, setUpgradeConfirmationData] = useState(null);
  const bookingDetails = JSON.parse(localStorage.getItem('bookingDetails'));
  const currentRoomType = bookingDetails?.primaryBooking?.room_type?.name || 'Room';
  const currentRoomPrice = bookingDetails?.primaryBooking?.payment_status?.total_amount || 0;

  // room type with bedtype factor names appended (uses the modification API's applied_factors)
  const currentRoomTypeWithFactors = getRoomTypeDisplayName(currentRoomType, appliedBedFactors);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get booking details from local storage
        const bookingDetails = JSON.parse(localStorage.getItem('bookingDetails'));
        if (!bookingDetails || !bookingDetails.primaryBooking) {
          throw new Error('Booking details not found');
        }

        // Set number of guests from booking details
        setNumberOfGuests(bookingDetails.primaryBooking.number_of_guests || 1);

        const { booking_id, checkin_date, checkout_date } = bookingDetails.primaryBooking;
        const today = new Date().toISOString().split('T')[0];

        // Set length of stay
        const checkin = new Date(checkin_date);
        const checkout = new Date(checkout_date);
        const stayLength = Math.ceil((checkout - checkin) / (1000 * 60 * 60 * 24));
        setLengthOfStay(stayLength);

        // Guests and property are fixed at check-in; only the room type can change here
        const guests = bookingDetails.primaryBooking.number_of_guests || 1;
        const propertyId = bookingDetails.primaryBooking.room_type?.property?.id;

        // Factors (add-ons) this booking already has, from the billing details
        let factorList = [];
        if (bookingDetails.order_id) {
          const billingResponse = await fetch(
            `${BQ_BASE_URL}/bq/api/checkin-billing-details/?order_id=${bookingDetails.order_id}`
          );
          const billingData = await billingResponse.json();
          setPaymentStatus(billingData.payment_summary);
          factorList = extractBookedFactors(billingData);
          setBookedFactors(factorList);
        }
        const factorIds = factorList.map((f) => f.factor_id).filter((id) => id != null);

        // Fetch modification options, priced for the booking's guests and selected factors
        const optionsParams = new URLSearchParams({
          new_checkin: checkin_date,
          new_checkout: checkout_date,
          number_of_guests: String(guests),
        });
        if (factorIds.length > 0) optionsParams.set('factor_id', factorIds.join(','));

        const modificationsResponse = await fetch(
          `${BQ_BASE_URL}/bq/api/modify/${booking_id}/options?${optionsParams.toString()}`
        );
        const modificationsData = await modificationsResponse.json();

        if (modificationsData.status === 'success') {
          // Filter out downgrade options, only keep upgrades
          const filteredData = {
            ...modificationsData.data,
            available_modifications:
              modificationsData.data.available_modifications?.filter(
                (option) => option.modification_type === 'upgrade'
              ) || [],
          };
          setModificationOptions(filteredData);

          // Extract bedtype factors from applied_factors (only these have factor_type)
          const bedFactors = (modificationsData.data?.applied_factors || []).filter(
            (f) => (f.factor_type || '').toLowerCase() === 'bedtype' && f.factorname
          );
          setAppliedBedFactors(bedFactors);
        } else {
          throw new Error(modificationsData.message || 'Failed to fetch modification options');
        }

        // Availability of every room type for the stay, limited to the selected factors
        if (modificationsData.data?.available_modifications?.length > 0) {
          try {
            const availabilityParams = new URLSearchParams({
              checkin: checkin_date,
              checkout: checkout_date,
            });
            if (propertyId) availabilityParams.set('property_id', String(propertyId));
            if (factorIds.length > 0) availabilityParams.set('factor_ids', factorIds.join(','));

            const availabilityResponse = await fetch(
              `${BQ_BASE_URL}/bq/api/availability/check-dates/all?${availabilityParams.toString()}`
            );
            const availabilityData = await availabilityResponse.json();

            const availabilityMap = {};
            const statusMap = {};
            (availabilityData.room_types || []).forEach((roomType) => {
              availabilityMap[roomType.room_type] = roomType.available_rooms || 0;
              statusMap[roomType.room_type] = {
                status: roomType.status,
                reason: roomType.status_reason,
              };
            });
            setRoomAvailability(availabilityMap);
            setRoomStatusInfo(statusMap);
          } catch (availabilityError) {
            console.error('Error fetching availability:', availabilityError);
          }
        }

        // Fetch amenities with booking ID
        const amenitiesResponse = await fetch(
          `${BQ_BASE_URL}/bq/api/amenityitems/checkin?bookingid=${booking_id}`
        );
        const amenitiesData = await amenitiesResponse.json();
        setAmenities(amenitiesData);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error(`Error: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Stay price of an upgrade before tax, including the booking's selected factors
  // (the options API prices them per room type); falls back to the plain room rate.
  const getUpgradeStayPrice = (upgrade) =>
    upgrade?.dynamic_pricing?.total_dynamic_price ?? upgrade.baseprice * lengthOfStay;

  const getUpgradePerNight = (upgrade) =>
    Math.round(getUpgradeStayPrice(upgrade) / Math.max(lengthOfStay, 1));

  const isRoomUnavailableForFactors = (roomType) =>
    roomStatusInfo[roomType]?.status === 'NOT_APPLICABLE';

  const getAvailabilityLabel = (roomType) => {
    const count = roomAvailability[roomType];
    if (isRoomUnavailableForFactors(roomType)) {
      return 'Not available with your selected add-ons';
    }
    if (count > 0) return `Only ${count} room${count !== 1 ? 's' : ''} available`;
    return 'Sold out';
  };

  const handleUpgradeSelect = (upgrade) => {
    setSelectedUpgrade(upgrade);
  };

  const handleRemoveUpgrade = (e) => {
    e.stopPropagation(); // Prevent event bubbling to parent div
    setSelectedUpgrade(null);
  };

  const openAmenityModal = (amenity) => {
    setCurrentAmenity(amenity);
    // Check if amenity is already selected and set quantity
    const existing = selectedAmenities.find((a) => a.id === amenity.id);
    setAmenityQuantity(existing ? existing.quantity : 1);
    setShowAmenityModal(true);
  };

  const closeAmenityModal = () => {
    setShowAmenityModal(false);
    setCurrentAmenity(null);
    setAmenityQuantity(1);
  };

  const openRoomImagesModal = (images, e) => {
    e.stopPropagation();
    setCurrentRoomImages(images);
    setCurrentImageIndex(0);
    setShowRoomImagesModal(true);
  };

  const closeRoomImagesModal = () => {
    setShowRoomImagesModal(false);
    setCurrentRoomImages([]);
    setCurrentImageIndex(0);
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev === currentRoomImages.length - 1 ? 0 : prev + 1));
  };

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? currentRoomImages.length - 1 : prev - 1));
  };

  const incrementQuantity = (amenity) => {
    const maxQty = amenity.max_quantity || 10;

    if (amenityQuantity < maxQty) {
      setAmenityQuantity((prev) => prev + 1);
    } else {
      toast.warn(
        "For larger requests or group arrangements, please contact the front desk. We'll be happy to assist you personally.",
        {
          position: 'top-center',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          toastId: 'maxQuantityReached',
          style: {
            maxWidth: '500px',
            marginTop: '',
            zIndex: 10000,
            whiteSpace: 'pre-line',
          },
        }
      );
    }
  };

  const decrementQuantity = (amenity) => {
    if (amenityQuantity > 1) {
      setAmenityQuantity((prev) => prev - 1);
    }
  };

  const saveAmenitySelection = () => {
    if (!currentAmenity) return;

    const maxQty = currentAmenity.max_quantity || 10;

    if (amenityQuantity > maxQty) {
      toast.warn(
        "For larger requests or group arrangements, please contact the front desk. We'll be happy to assist you personally.",
        {
          position: 'top-center',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          style: {
            marginTop: '10px',
            zIndex: 1000,
          },
        }
      );
      return;
    }

    setSelectedAmenities((prev) => {
      const filtered = prev.filter((a) => a.id !== currentAmenity.id);
      if (amenityQuantity > 0) {
        return [
          ...filtered,
          {
            ...currentAmenity,
            quantity: amenityQuantity,
          },
        ];
      }
      return filtered;
    });

    closeAmenityModal();
  };

  const removeAmenity = (amenityId, e) => {
    e.stopPropagation();
    setSelectedAmenities((prev) => prev.filter((a) => a.id !== amenityId));
  };

  const scrollAmenities = (direction) => {
    const amenitiesContainer = document.getElementById('amenities-container');
    const scrollAmount = 300;
    if (direction === 'left') {
      amenitiesContainer.scrollLeft -= scrollAmount;
    } else {
      amenitiesContainer.scrollLeft += scrollAmount;
    }
    setAmenitiesScrollLeft(amenitiesContainer.scrollLeft);
  };

  // ============================================================
  // handleAutoAssign:
  //   - Calls the backend /checkin-auto-assign endpoint
  //   - The backend returns each assigned room with `room_type_with_bed`
  //     (e.g. "Premium - Single Queen Bed") alongside the raw `room_type`
  //   - We normalize each room by adding `room_display_name`
  //     (which prefers `room_type_with_bed` over `room_type`)
  //   - The full normalized array is persisted to localStorage
  //     under the key `assignedRooms` so step 4 can render it.
  // ============================================================
  const handleAutoAssign = async () => {
    try {
      const bookingDetails = JSON.parse(localStorage.getItem('bookingDetails'));
      if (!bookingDetails || !bookingDetails.order_id) {
        throw new Error('Booking details not found');
      }

      const response = await fetch(
        `${BQ_BASE_URL}/bq/api/checkin-auto-assign/?order_id=${bookingDetails.order_id}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();

      if (response.ok) {
        // Normalize each assigned room: add `room_display_name` that prefers
        // the backend's `room_type_with_bed` (e.g. "Premium - Single Queen Bed")
        // and falls back to `room_type` (e.g. "Premium") if not provided.
        const normalizedRooms = (data.assigned_rooms || []).map((room) => ({
          ...room,
          room_display_name: getAssignedRoomDisplayName(room),
        }));

        // Persist to localStorage — step 4 (RoomAssignment) reads this.
        localStorage.setItem('assignedRooms', JSON.stringify(normalizedRooms));
        // Proceed to check-in step
        setCurrentStep(4);
        return;
      }

      // Any non-OK response that indicates no room could be assigned → single message
      if (response.status === 409) {
        toast.error(
          `No rooms available for selected Add-ons, Please contact the front desk for assistance.`,
          {
            position: 'top-center',
            autoClose: 9000,
            style: { whiteSpace: 'pre-line', maxWidth: '600px' },
            toastId: 'assignFailure',
          }
        );
        return;
      }

      // Fallback for other non-OK responses
      throw new Error(data?.message || data?.detail?.message || 'Failed to auto assign rooms');
    } catch (error) {
      toast.error(`Error: ${error.message}`, {
        position: 'top-center',
        autoClose: 6000,
      });
    }
  };
  // ============================================================
  // END handleAutoAssign
  // ============================================================

  const handleAmenitiesSubmit = async () => {
    try {
      const bookingDetails = JSON.parse(localStorage.getItem('bookingDetails'));
      if (!bookingDetails || !bookingDetails.primaryBooking) {
        throw new Error('Booking details not found');
      }

      const { booking_id } = bookingDetails.primaryBooking;
      const today = new Date().toISOString();

      const amenitiesData = {
        bookingid: booking_id,
        items: selectedAmenities.map((amenity) => ({
          itemid: amenity.id,
          quantity: amenity.quantity,
          urgencylevel: 'Normal',
          scheduledtime: today,
          specialinstructions: '',
        })),
      };

      const response = await fetch(`${BQ_BASE_URL}/bq/api/amenityrequests/with-billing`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(amenitiesData),
      });

      const data = await response.json();

      if (response.ok) {
        if (bookingDetails.order_id) {
          const paymentResponse = await fetch(
            `${BQ_BASE_URL}/bq/api/checkin-billing-details/?order_id=${bookingDetails.order_id}`
          );
          const paymentData = await paymentResponse.json();
          setPaymentStatus(paymentData.payment_summary);
        }
        return true;
      } else {
        throw new Error(data.message || 'Failed to add amenities');
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
      return false;
    }
  };

  const checkPaymentStatus = async () => {
    try {
      const bookingDetails = JSON.parse(localStorage.getItem('bookingDetails'));
      if (!bookingDetails || !bookingDetails.order_id) {
        throw new Error('Booking details not found');
      }

      const paymentResponse = await fetch(
        `${BQ_BASE_URL}/bq/api/checkin-billing-details/?order_id=${bookingDetails.order_id}`
      );
      const paymentData = await paymentResponse.json();
      setPaymentStatus(paymentData.payment_summary);

      return paymentData.payment_summary?.fully_paid || false;
    } catch (error) {
      console.error('Error checking payment status:', error);
      return false;
    }
  };

  const handleModificationSubmit = async () => {
    try {
      const bookingDetails = JSON.parse(localStorage.getItem('bookingDetails'));
      if (!bookingDetails || !bookingDetails.primaryBooking) {
        throw new Error('Booking details not found');
      }

      const { booking_id, checkin_date, checkout_date } = bookingDetails.primaryBooking;

      // Only the room type changes at check-in, so the factors and the guest count stay as
      // booked; the backend prices them for the new room type.
      const guestCount = bookingDetails.primaryBooking.number_of_guests || 1;
      const factorPayload = bookedFactors.map((f) => ({
        factorid: f.factor_id,
        factorname: f.factor_name,
      }));

      const apiUrl = `${BQ_BASE_URL}/bq/api/modify/${booking_id}/modify?new_room_type_name=${encodeURIComponent(
        selectedUpgrade.roomtypename
      )}&new_checkin_date=${checkin_date}&new_checkout_date=${checkout_date}`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          selected_factors: factorPayload,
          previous_factors: factorPayload,
          number_of_guests: guestCount,
          previous_number_of_guests: guestCount,
        }),
      });

      const data = await response.json();

      if (response.ok && data.status === 'success') {
        localStorage.setItem(
          'roomModification',
          JSON.stringify({
            newRoomType: selectedUpgrade.roomtypename,
            effectiveDate: new Date().toISOString().split('T')[0],
            modificationType: 'upgrade',
            priceDifference: data.data.price_difference,
            netPriceImpact: data.data.net_price_impact,
            oldTotal: data.data.old_total,
            newTotal: data.data.new_total,
            financialAction: data.data.financial_action,
            policyStatus: data.data.policy.modification_status,
            isRestricted: data.data.redzone.is_restricted,
            redzoneStatus: data.data.redzone.redzone_status,
            fullResponse: data,
          })
        );

        const updatedBookingDetails = {
          ...bookingDetails,
          primaryBooking: {
            ...bookingDetails.primaryBooking,
            room_type: {
              ...bookingDetails.primaryBooking.room_type,
              name: selectedUpgrade.roomtypename,
            },
            payment_status: {
              ...bookingDetails.primaryBooking.payment_status,
              total_amount: data.data.new_total,
            },
          },
        };
        localStorage.setItem('bookingDetails', JSON.stringify(updatedBookingDetails));

        if (bookingDetails.order_id) {
          const paymentResponse = await fetch(
            `${BQ_BASE_URL}/bq/api/checkin-billing-details/?order_id=${bookingDetails.order_id}`
          );
          const paymentData = await paymentResponse.json();
          setPaymentStatus(paymentData.payment_summary);
        }

        toast.success('Room upgrade applied successfully!', {
          position: 'top-center',
          autoClose: 3000,
        });

        return true;
      } else {
        throw new Error(data.message || 'Failed to apply room modification');
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`, {
        position: 'top-center',
        autoClose: 5000,
      });
      return false;
    }
  };

  const showUpgradeConfirmationModal = () => {
    if (!selectedUpgrade) return false;

    const upgradePrice = getUpgradeStayPrice(selectedUpgrade);
    const totalAmenitiesPrice = selectedAmenities.reduce(
      (total, amenity) => total + amenity.baseprice * amenity.quantity,
      0
    );
    const totalAdditionalCost = upgradePrice + totalAmenitiesPrice;

    setUpgradeConfirmationData({
      currentRoom: currentRoomTypeWithFactors,
      currentPrice: currentRoomPrice,
      newRoom: getRoomTypeDisplayName(selectedUpgrade.roomtypename, appliedBedFactors),
      upgradePrice: upgradePrice,
      amenitiesPrice: totalAmenitiesPrice,
      totalAdditionalCost: totalAdditionalCost,
      nights: lengthOfStay,
      perNightPrice: getUpgradePerNight(selectedUpgrade),
      images: selectedUpgrade.images,
    });

    setShowUpgradeConfirmation(true);
    return true;
  };

  const confirmUpgrade = async () => {
    setShowUpgradeConfirmation(false);
    await proceedWithUpgrade();
  };

  const cancelUpgrade = () => {
    setShowUpgradeConfirmation(false);
  };

  const proceedWithUpgrade = async () => {
    try {
      let proceed = true;
      let hasModification = false;

      if (selectedAmenities.length > 0) {
        proceed = await handleAmenitiesSubmit();
      }

      if (proceed && selectedUpgrade) {
        hasModification = true;
        proceed = await handleModificationSubmit();

        if (proceed) {
          const modificationData = JSON.parse(localStorage.getItem('roomModification'));
        }
      }

      const isFullyPaid = await checkPaymentStatus();

      if (proceed) {
        if (isFullyPaid) {
          if (!hasModification || (hasModification && paymentStatus?.fully_paid)) {
            await handleAutoAssign();
          } else {
            setCurrentStep(3);
          }
        } else {
          setCurrentStep(3);
        }
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    }
  };

  const handleContinue = async () => {
    try {
      if (selectedUpgrade) {
        showUpgradeConfirmationModal();
      } else {
        await proceedWithUpgrade();
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    }
  };

  const handleSkip = async () => {
    try {
      const isFullyPaid = await checkPaymentStatus();

      if (isFullyPaid) {
        await handleAutoAssign();
      } else {
        setCurrentStep(3);
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading enhancements...</div>;
  }

  return (
    <div className="">
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-4 gap-3">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Enhance Your Stay</h2>
            <p className="text-gray-600 text-sm mt-1">
              Enhance your reservation with room upgrades and relaxing amenities
            </p>
          </div>

          {currentRoomType && (
            <div className="text-left sm:text-right">
              <h3 className="font-semibold text-sm text-gray-600">Your Current Room</h3>
              <div className="flex flex-wrap sm:flex-nowrap items-center justify-start sm:justify-end gap-2">
                {/* Room type with bedtype factor names appended */}
                <h4 className="font-bold text-lg">{currentRoomTypeWithFactors}</h4>
                <span className="font-bold text-lg">-</span>
                <p className="font-bold text-lg">₹{currentRoomPrice.toLocaleString()}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Stay details: fixed at check-in, only the room type can be changed */}
      <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
        <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Check-in</p>
            <p className="font-semibold text-gray-900">
              {formatStayDate(bookingDetails?.primaryBooking?.checkin_date)}
            </p>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Check-out</p>
            <p className="font-semibold text-gray-900">
              {formatStayDate(bookingDetails?.primaryBooking?.checkout_date)}
            </p>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Stay</p>
            <p className="font-semibold text-gray-900">
              {lengthOfStay} night{lengthOfStay !== 1 ? 's' : ''} · {numberOfGuests} guest
              {numberOfGuests !== 1 ? 's' : ''}
            </p>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Add-ons</p>
            <p className="font-semibold text-gray-900">
              {bookedFactors.length > 0
                ? bookedFactors.map((f) => f.factor_name).join(', ')
                : 'None'}
            </p>
          </div>
        </div>
        <p className="mt-3 text-xs text-gray-500">
          Dates, guests and add-ons stay as booked. You can only change the room type here.
        </p>
      </div>

      {/* Room Modification Options Section */}
      <div className="mb-8">
        <h3 className="font-semibold text-lg mb-1">Room Upgrade Options</h3>
        <p className="mb-3 text-xs text-gray-500">
          Prices and availability are for your selected add-ons.
        </p>
        {modificationOptions?.available_modifications?.length > 0 ? (
          <div className="grid lg:grid-cols-2 gap-4 md:grid-cols-2">
            {modificationOptions.available_modifications.map((upgrade) => (
              <div
                key={upgrade.roomtypename}
                className={`border rounded-lg p-0 transition-all flex h-full ${
                  selectedUpgrade?.roomtypename === upgrade.roomtypename
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="w-2/4 h-full overflow-hidden rounded-l-lg relative">
                  <div className="absolute inset-0">
                    <img
                      src={upgrade.images[0]}
                      alt={upgrade.roomtypename}
                      className="w-full h-full object-cover"
                    />
                    {upgrade.images.length > 1 && (
                      <>
                        <div className="absolute top-0 left-0 w-full h-full flex items-center justify-between px-2">
                          <button
                            className="bg-black bg-opacity-30 text-white p-1 rounded-full hover:bg-opacity-50 z-10"
                            onClick={(e) => {
                              e.stopPropagation();
                              const newIndex =
                                currentImageIndex === 0
                                  ? upgrade.images.length - 1
                                  : currentImageIndex - 1;
                              setCurrentImageIndex(newIndex);
                            }}
                          >
                            <FaChevronLeft />
                          </button>
                          <button
                            className="bg-black bg-opacity-30 text-white p-1 rounded-full hover:bg-opacity-50 z-10"
                            onClick={(e) => {
                              e.stopPropagation();
                              const newIndex =
                                currentImageIndex === upgrade.images.length - 1
                                  ? 0
                                  : currentImageIndex + 1;
                              setCurrentImageIndex(newIndex);
                            }}
                          >
                            <FaChevronRight />
                          </button>
                        </div>
                        <div className="absolute bottom-2 left-0 w-full flex justify-center space-x-1">
                          {upgrade.images.map((_, index) => (
                            <div
                              key={index}
                              className={`w-2 h-2 rounded-full ${
                                currentImageIndex === index ? 'bg-white' : 'bg-white bg-opacity-50'
                              }`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                    <button
                      onClick={(e) => openRoomImagesModal(upgrade.images, e)}
                      className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white p-1 rounded hover:bg-opacity-70"
                    >
                      <FaExpand size={12} />
                    </button>
                  </div>
                </div>

                <div className="w-1/2 flex flex-col p-4 ">
                  <div>
                    {/* Show upgrade room type with bedtype factor names */}
                    <h4 className="font-bold text-lg mb-2">
                      {getRoomTypeDisplayName(upgrade.roomtypename, appliedBedFactors)}
                    </h4>
                    <p className="text-black  text-xs">
                      ₹{getUpgradePerNight(upgrade).toLocaleString()}
                      <span className="text-black text-xs ml-1">/ Per night</span>
                    </p>
                    <p className="text-black font-bold text-lg">
                      ₹{getUpgradeStayPrice(upgrade).toLocaleString()}
                      <span className="text-black font-bold ml-1">
                        / {lengthOfStay} Night{lengthOfStay !== 1 ? 's' : ''}
                      </span>
                    </p>

                    <ul className="text-xs text-gray-500 space-y-1 mt-2">
                      <li className="flex items-start">
                        <span className="inline-block w-1 h-1 mt-1.5 mr-1.5 bg-gray-400 rounded-full flex-shrink-0"></span>
                        <span>Excluding taxes and fees</span>
                      </li>
                      <li className="flex items-start">
                        <span className="inline-block w-1 h-1 mt-1.5 mr-1.5 bg-gray-400 rounded-full flex-shrink-0"></span>
                        <span>Available immediately</span>
                      </li>
                      <li className="flex items-start">
                        <span className="inline-block w-1 h-1 mt-1.5 mr-1.5 bg-gray-400 rounded-full flex-shrink-0"></span>
                        <span>
                          {roomAvailability[upgrade.roomtypename] !== undefined && (
                            <div
                              className={`text-xs mb-2 ${
                                roomAvailability[upgrade.roomtypename] > 5
                                  ? 'text-green-600'
                                  : 'text-red-600'
                              }`}
                            >
                              {getAvailabilityLabel(upgrade.roomtypename)}
                            </div>
                          )}
                        </span>
                      </li>
                    </ul>
                  </div>

                  <div className="mt-auto pt-4 flex justify-end">
                    {selectedUpgrade?.roomtypename === upgrade.roomtypename ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveUpgrade(e);
                        }}
                        className="w-1/2 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded-md transition font-medium"
                      >
                        Remove
                      </button>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUpgradeSelect(upgrade);
                        }}
                        disabled={roomAvailability[upgrade.roomtypename] === 0}
                        className={`w-1/2 py-1.5 bg-black hover:bg-gray-800 text-white rounded-md transition font-medium ${
                          roomAvailability[upgrade.roomtypename] === 0
                            ? 'opacity-50 cursor-not-allowed'
                            : ''
                        }`}
                      >
                        {isRoomUnavailableForFactors(upgrade.roomtypename)
                          ? 'Unavailable'
                          : roomAvailability[upgrade.roomtypename] === 0
                            ? 'Sold Out'
                            : 'Select'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-gray-600 py-1 text-sm">
            No room upgrades available, No further upgrades are available as you've already selected
            the top-tier room option
          </div>
        )}
      </div>

      {/* Amenities Section */}
      <div className="mb-8 relative">
        <h3 className="font-semibold text-lg mb-3">Additional Amenities</h3>
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm text-gray-600">
            {selectedAmenities.length > 0
              ? `${selectedAmenities.reduce(
                  (total, amenity) => total + amenity.quantity,
                  0
                )} items selected`
              : 'No amenities selected'}
          </span>
          {selectedAmenities.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold bg-gray-100 px-3 py-1 rounded-full">
                Total: ₹
                {selectedAmenities
                  .reduce((total, amenity) => total + amenity.baseprice * amenity.quantity, 0)
                  .toLocaleString()}
              </span>
            </div>
          )}
        </div>
        <div className="relative">
          {amenitiesScrollLeft > 0 && (
            <button
              onClick={() => scrollAmenities('left')}
              className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-white rounded-full p-2 shadow-md hover:bg-gray-100"
              style={{ marginLeft: '-16px' }}
            >
              <FaChevronLeft className="text-gray-600" />
            </button>
          )}
          <div
            id="amenities-container"
            className="flex overflow-x-auto space-x-4 pb-4 scroll-smooth"
            style={{ scrollbarWidth: 'none' }}
          >
            {amenities.map((amenity) => {
              const isSelected = selectedAmenities.some((a) => a.id === amenity.id);
              const selectedItem = isSelected
                ? selectedAmenities.find((a) => a.id === amenity.id)
                : null;
              const totalPrice = isSelected ? amenity.baseprice * selectedItem.quantity : 0;

              return (
                <div
                  key={amenity.id}
                  className={`flex-shrink-0 border rounded-lg p-0 cursor-pointer transition-all flex ${
                    isSelected ? 'h-42 w-96' : 'h-44 w-80'
                  } ${
                    isSelected
                      ? 'border-blue-300 bg-blue-50 shadow-md'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => openAmenityModal(amenity)}
                >
                  <div
                    className={`${
                      isSelected ? 'w-2/5' : 'w-2/3'
                    } h-full overflow-hidden rounded-l-lg relative`}
                  >
                    <img
                      src={amenity.imageurl}
                      alt={amenity.name}
                      className="w-full h-full object-cover"
                    />
                    <div
                      className={`absolute top-2 right-2 text-xs px-2 py-1 rounded-full ${
                        amenity.refundable
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {amenity.refundable ? 'Refundable' : 'Non-refundable'}
                    </div>
                  </div>

                  <div
                    className={`${
                      isSelected ? 'w-3/5' : 'w-2/3'
                    } p-3 flex flex-col justify-between`}
                  >
                    <div>
                      <h4 className="font-medium text-sm line-clamp-1">{amenity.name}</h4>
                      <div className="flex items-baseline mt-1">
                        <span className="text-black font-semibold text-sm">
                          ₹{amenity.baseprice?.toLocaleString()}
                          <span className="text-gray-500 text-xs lowercase ml-1">
                            /{amenity.price_type}
                          </span>
                        </span>
                      </div>
                    </div>

                    {isSelected ? (
                      <div className="flex flex-col">
                        <div className="flex justify-between items-center text-xs mb-1">
                          <span className="text-gray-600">Quantity:</span>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{selectedItem.quantity}</span>
                          </div>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 text-xs">Total:</span>
                          <span className="text-black font-semibold text-sm">
                            ₹{totalPrice.toLocaleString()}
                          </span>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeAmenity(amenity.id, e);
                          }}
                          className="mt-2 py-1.5 px-3 rounded text-xs font-medium transition self-end bg-red-500 hover:bg-red-600 text-white"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openAmenityModal(amenity);
                        }}
                        className="py-1.5 px-6 rounded text-xs font-medium transition self-end bg-black hover:bg-gray-800 text-white"
                      >
                        Add
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <button
            onClick={() => scrollAmenities('right')}
            className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-white rounded-full p-2 shadow-md hover:bg-gray-100"
            style={{ marginRight: '-16px' }}
          >
            <FaChevronRight className="text-gray-600" />
          </button>
        </div>
      </div>

      {/* Amenity Quantity Modal */}
      {showAmenityModal && currentAmenity && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <ToastContainer
            position="top-center"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">{currentAmenity.name}</h3>
              <button onClick={closeAmenityModal} className="text-gray-500 hover:text-gray-700">
                <FaTimes />
              </button>
            </div>

            <p className="text-gray-600 text-sm mb-6">
              Select the number of people for whom you'd like to include this service.
            </p>

            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">Quantity:</span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => decrementQuantity(currentAmenity)}
                    className={`w-8 h-8 flex items-center justify-center border rounded-full hover:bg-gray-100 ${
                      amenityQuantity <= 1 ? 'text-gray-400 cursor-not-allowed' : ''
                    }`}
                    disabled={amenityQuantity <= 1}
                  >
                    <FaMinus />
                  </button>
                  <span className="mx-4 text-lg font-medium">{amenityQuantity}</span>
                  <button
                    onClick={() => incrementQuantity(currentAmenity)}
                    className={`w-8 h-8 flex items-center justify-center border rounded-full hover:bg-gray-100 ${
                      amenityQuantity >= currentAmenity.max_quantity
                        ? 'text-gray-400 cursor-not-allowed'
                        : ''
                    }`}
                  >
                    <FaPlus />
                  </button>
                </div>
              </div>
            </div>

            <div className="space-y-2 mb-6">
              <div className="flex justify-between">
                <span className="text-gray-600">Price per item:</span>
                <span className="font-medium">
                  ₹{currentAmenity.baseprice}
                  <span className="text-gray-500 text-xs text-transform: lowercase ">
                    /{currentAmenity.price_type}
                  </span>
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Total price:</span>
                <span className="text-black font-semibold">
                  ₹{(currentAmenity.baseprice * amenityQuantity).toLocaleString()}{' '}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 text-sm">
                  {currentAmenity.value === 0 ? 'Tax not applicable' : 'Excluding taxes and fees'}
                </span>
              </div>
            </div>

            <div className="flex justify-end space-x-4 pt-4 border-t">
              <button
                onClick={closeAmenityModal}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={saveAmenitySelection}
                className="px-6 py-2 bg-black text-white rounded-md hover:text-gray-300"
              >
                {selectedAmenities.some((a) => a.id === currentAmenity.id) ? 'Update' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Room Images Modal */}
      {showRoomImagesModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="relative w-full max-w-3xl max-h-[60vh] bg-white rounded-xl shadow-xl flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h1 className="text-lg font-semibold">View Room Images</h1>
              <button
                onClick={closeRoomImagesModal}
                className="text-gray-500 hover:text-gray-700 transition text-2xl"
              >
                <FaTimes />
              </button>
            </div>

            <div className="relative flex-1 flex items-center justify-center p-4 overflow-hidden">
              <img
                src={currentRoomImages[currentImageIndex]}
                alt={`Room ${currentImageIndex + 1}`}
                className="max-w-full max-h-[calc(60vh-120px)] object-contain"
              />

              <button
                className="absolute left-2 md:left-4 top-1/2 transform -translate-y-1/2 bg-gray-800 bg-opacity-60 text-white p-2 rounded-full hover:bg-opacity-80 transition z-50"
                onClick={handlePrevImage}
              >
                <FaChevronLeft size={20} />
              </button>
              <button
                className="absolute right-2 md:right-4 top-1/2 transform -translate-y-1/2 bg-gray-800 bg-opacity-60 text-white p-2 rounded-full hover:bg-opacity-80 transition z-50"
                onClick={handleNextImage}
              >
                <FaChevronRight size={20} />
              </button>
            </div>

            <div className="p-4 flex justify-center">
              <div className="flex space-x-2">
                {currentRoomImages.map((_, index) => (
                  <button
                    key={index}
                    className={`w-2 h-2 md:w-3 md:h-3 rounded-full ${
                      currentImageIndex === index ? 'bg-gray-800' : 'bg-gray-300'
                    }`}
                    onClick={() => setCurrentImageIndex(index)}
                    aria-label={`Go to image ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Room Upgrade Confirmation Modal */}
      {showUpgradeConfirmation && upgradeConfirmationData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden">
            <div className="bg-yellow-50 border-b border-yellow-200 p-6">
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                    <FaExclamationTriangle className="text-yellow-600 text-xl" />
                  </div>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Confirm Room Upgrade</h2>
                  <p className="text-gray-600 mt-1">
                    This action cannot be undone. Please review your upgrade details below.
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 max-h-[60vh] overflow-y-auto">
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Current Room</h3>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">
                    {upgradeConfirmationData.currentRoom}
                  </h4>

                  <div className="mt-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="w-2 h-2 bg-gray-400 rounded-full mr-2"></span>
                      Your current selection
                    </div>
                  </div>
                </div>

                <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Upgrading To</h3>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">
                    {upgradeConfirmationData.newRoom}
                  </h4>
                  <div className="space-y-1">
                    <p className="text-sm text-gray-600">
                      ₹{upgradeConfirmationData.perNightPrice.toLocaleString()} / night
                    </p>
                    <p className="text-2xl font-bold text-blue-700">
                      ₹{upgradeConfirmationData.upgradePrice.toLocaleString()}
                      <span className="text-sm font-normal text-gray-600 ml-2">
                        for {upgradeConfirmationData.nights} night
                        {upgradeConfirmationData.nights !== 1 ? 's' : ''}
                      </span>
                    </p>
                  </div>
                  {upgradeConfirmationData.images && upgradeConfirmationData.images.length > 0 && (
                    <div className="mt-4">
                      <img
                        src={upgradeConfirmationData.images[0]}
                        alt={upgradeConfirmationData.newRoom}
                        className="w-full h-32 object-cover rounded-md"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg p-4 mb-6">
                <h3 className="font-medium text-gray-900 mb-3">Additional Costs</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      Room Upgrade ({upgradeConfirmationData.nights} night
                      {upgradeConfirmationData.nights !== 1 ? 's' : ''}):
                    </span>
                    <span className="font-medium">
                      ₹{upgradeConfirmationData.upgradePrice.toLocaleString()}
                    </span>
                  </div>
                  {upgradeConfirmationData.amenitiesPrice > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Amenities:</span>
                      <span className="font-medium">
                        ₹{upgradeConfirmationData.amenitiesPrice.toLocaleString()}
                      </span>
                    </div>
                  )}
                  <div className="border-t pt-2 mt-2">
                    <div className="flex justify-between">
                      <span className="font-bold text-gray-900">Total Additional Cost:</span>
                      <span className="font-bold text-lg text-blue-700">
                        ₹{upgradeConfirmationData.totalAdditionalCost.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-medium text-yellow-800 mb-2">Important Notes:</h4>
                <ul className="text-sm text-yellow-700 space-y-1">
                  <li className="flex items-start">
                    <span className="inline-block w-1 h-1 mt-1.5 mr-2 bg-yellow-500 rounded-full flex-shrink-0"></span>
                    This upgrade is <span className="font-bold">irreversible</span> and cannot be
                    undone
                  </li>
                  <li className="flex items-start">
                    <span className="inline-block w-1 h-1 mt-1.5 mr-2 bg-yellow-500 rounded-full flex-shrink-0"></span>
                    The new room will be assigned immediately after confirmation
                  </li>
                  <li className="flex items-start">
                    <span className="inline-block w-1 h-1 mt-1.5 mr-2 bg-yellow-500 rounded-full flex-shrink-0"></span>
                    You may need to pay any additional charges before check-in
                  </li>
                  <li className="flex items-start">
                    <span className="inline-block w-1 h-1 mt-1.5 mr-2 bg-yellow-500 rounded-full flex-shrink-0"></span>
                    All prices are exclusive of taxes and fees
                  </li>
                </ul>
              </div>
            </div>

            <div className="border-t border-gray-200 p-6">
              <div className="flex flex-col sm:flex-row justify-end gap-3">
                <button
                  onClick={cancelUpgrade}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmUpgrade}
                  className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 font-medium transition flex items-center justify-center"
                >
                  Yes, Upgrade My Room
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col md:flex-row justify-between gap-4">
        <button
          onClick={() => setCurrentStep(currentStep - 1)}
          className="w-full md:w-1/6 bg-gray-200 text-gray-800 py-2 rounded hover:bg-gray-300 transition font-medium"
        >
          Back
        </button>

        <div className="flex flex-col md:flex-row gap-4 w-full md:w-auto">
          <button
            onClick={handleSkip}
            className="w-full md:w-[250px] bg-gray-200 text-gray-800 py-2 rounded hover:bg-gray-300 transition font-medium"
          >
            Skip
          </button>
          <button
            onClick={handleContinue}
            className="w-full md:w-[250px] bg-black text-white py-2 rounded hover:bg-gray-800 transition font-medium"
          >
            {selectedUpgrade || selectedAmenities.length > 0 ? 'Confirm Purchase' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EnhanceStay;

// import { useEffect, useState } from 'react';
// import { ToastContainer, toast } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';
// import {
//   FaChevronLeft,
//   FaChevronRight,
//   FaTimes,
//   FaExpand,
//   FaPlus,
//   FaMinus,
//   FaExclamationTriangle,
// } from 'react-icons/fa';

// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// const EnhanceStay = ({ currentStep, setCurrentStep, totalSteps }) => {
//   const [modificationOptions, setModificationOptions] = useState(null);
//   const [amenities, setAmenities] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [selectedUpgrade, setSelectedUpgrade] = useState(null);
//   const [selectedAmenities, setSelectedAmenities] = useState([]);
//   const [amenitiesScrollLeft, setAmenitiesScrollLeft] = useState(0);
//   const [paymentStatus, setPaymentStatus] = useState(null);
//   const [showAmenityModal, setShowAmenityModal] = useState(false);
//   const [currentAmenity, setCurrentAmenity] = useState(null);
//   const [amenityQuantity, setAmenityQuantity] = useState(1);
//   const [numberOfGuests, setNumberOfGuests] = useState(1);
//   const [showRoomImagesModal, setShowRoomImagesModal] = useState(false);
//   const [currentRoomImages, setCurrentRoomImages] = useState([]);
//   const [currentImageIndex, setCurrentImageIndex] = useState(0);
//   const [roomAvailability, setRoomAvailability] = useState({});
//   const [lengthOfStay, setLengthOfStay] = useState(1);
//   const [showUpgradeConfirmation, setShowUpgradeConfirmation] = useState(false);
//   const [upgradeConfirmationData, setUpgradeConfirmationData] = useState(null);
//   const bookingDetails = JSON.parse(localStorage.getItem('bookingDetails'));
//   const currentRoomType = bookingDetails?.primaryBooking?.room_type?.name || 'Room';
//   const currentRoomPrice = bookingDetails?.primaryBooking?.payment_status?.total_amount || 0;

//   useEffect(() => {
//     const fetchData = async () => {
//       try {
//         // Get booking details from local storage
//         const bookingDetails = JSON.parse(localStorage.getItem('bookingDetails'));
//         if (!bookingDetails || !bookingDetails.primaryBooking) {
//           throw new Error('Booking details not found');
//         }

//         // Set number of guests from booking details
//         setNumberOfGuests(bookingDetails.primaryBooking.number_of_guests || 1);

//         const { booking_id, checkin_date, checkout_date } = bookingDetails.primaryBooking;
//         const today = new Date().toISOString().split('T')[0];

//         // Set length of stay
//         const checkin = new Date(checkin_date);
//         const checkout = new Date(checkout_date);
//         const stayLength = Math.ceil((checkout - checkin) / (1000 * 60 * 60 * 24));
//         setLengthOfStay(stayLength);

//         // Fetch modification options using the new API
//         const modificationsResponse = await fetch(
//           `${BQ_BASE_URL}/bq/api/modify/${booking_id}/options?new_checkin=${checkin_date}&new_checkout=${checkout_date}`
//         );
//         const modificationsData = await modificationsResponse.json();

//         if (modificationsData.status === 'success') {
//           // Filter out downgrade options, only keep upgrades
//           const filteredData = {
//             ...modificationsData.data,
//             available_modifications:
//               modificationsData.data.available_modifications?.filter(
//                 (option) => option.modification_type === 'upgrade'
//               ) || [],
//           };
//           setModificationOptions(filteredData);
//         } else {
//           throw new Error(modificationsData.message || 'Failed to fetch modification options');
//         }

//         // Fetch availability for each room type (only for upgrades)
//         if (modificationsData.data?.available_modifications?.length > 0) {
//           const upgradeOptions = modificationsData.data.available_modifications.filter(
//             (option) => option.modification_type === 'upgrade'
//           );

//           const availabilityPromises = upgradeOptions.map(async (upgrade) => {
//             const response = await fetch(
//               `${BQ_BASE_URL}/bq/api/availability-room-type?room_type=${encodeURIComponent(
//                 upgrade.roomtypename
//               )}`
//             );
//             const data = await response.json();
//             return {
//               roomType: upgrade.roomtypename,
//               count: data.available_rooms?.length || 0,
//             };
//           });

//           const availabilityResults = await Promise.all(availabilityPromises);
//           const availabilityMap = {};
//           availabilityResults.forEach((result) => {
//             availabilityMap[result.roomType] = result.count;
//           });
//           setRoomAvailability(availabilityMap);
//         }

//         // Fetch amenities with booking ID
//         const amenitiesResponse = await fetch(
//           `${BQ_BASE_URL}/bq/api/amenityitems/checkin?bookingid=${booking_id}`
//         );
//         const amenitiesData = await amenitiesResponse.json();
//         setAmenities(amenitiesData);

//         // Fetch payment status
//         if (bookingDetails.order_id) {
//           const paymentResponse = await fetch(
//             `${BQ_BASE_URL}/bq/api/checkin-billing-details/?order_id=${bookingDetails.order_id}`
//           );
//           const paymentData = await paymentResponse.json();
//           setPaymentStatus(paymentData.payment_summary);
//         }
//       } catch (error) {
//         console.error('Error fetching data:', error);
//         toast.error(`Error: ${error.message}`);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchData();
//   }, []);

//   const handleUpgradeSelect = (upgrade) => {
//     setSelectedUpgrade(upgrade);
//   };

//   const handleRemoveUpgrade = (e) => {
//     e.stopPropagation(); // Prevent event bubbling to parent div
//     setSelectedUpgrade(null);
//   };

//   const openAmenityModal = (amenity) => {
//     setCurrentAmenity(amenity);
//     // Check if amenity is already selected and set quantity
//     const existing = selectedAmenities.find((a) => a.id === amenity.id);
//     setAmenityQuantity(existing ? existing.quantity : 1);
//     setShowAmenityModal(true);
//   };

//   const closeAmenityModal = () => {
//     setShowAmenityModal(false);
//     setCurrentAmenity(null);
//     setAmenityQuantity(1);
//   };

//   const openRoomImagesModal = (images, e) => {
//     e.stopPropagation();
//     setCurrentRoomImages(images);
//     setCurrentImageIndex(0);
//     setShowRoomImagesModal(true);
//   };

//   const closeRoomImagesModal = () => {
//     setShowRoomImagesModal(false);
//     setCurrentRoomImages([]);
//     setCurrentImageIndex(0);
//   };

//   const handleNextImage = () => {
//     setCurrentImageIndex((prev) => (prev === currentRoomImages.length - 1 ? 0 : prev + 1));
//   };

//   const handlePrevImage = () => {
//     setCurrentImageIndex((prev) => (prev === 0 ? currentRoomImages.length - 1 : prev - 1));
//   };

//   const incrementQuantity = (amenity) => {
//     const maxQty = amenity.max_quantity || 10;

//     if (amenityQuantity < maxQty) {
//       setAmenityQuantity((prev) => prev + 1);
//     } else {
//       toast.warn(
//         "For larger requests or group arrangements, please contact the front desk. We'll be happy to assist you personally.",
//         {
//           position: 'top-center',
//           autoClose: 5000,
//           hideProgressBar: false,
//           closeOnClick: true,
//           pauseOnHover: true,
//           draggable: true,
//           toastId: 'maxQuantityReached',
//           style: {
//             maxWidth: '500px', // Increase max width
//             marginTop: '',
//             zIndex: 10000,
//             whiteSpace: 'pre-line', // Allow text to wrap
//           },
//         }
//       );
//     }
//   };

//   const decrementQuantity = (amenity) => {
//     if (amenityQuantity > 1) {
//       setAmenityQuantity((prev) => prev - 1);
//     }
//   };

//   const saveAmenitySelection = () => {
//     if (!currentAmenity) return;

//     const maxQty = currentAmenity.max_quantity || 10;

//     if (amenityQuantity > maxQty) {
//       toast.warn(
//         "For larger requests or group arrangements, please contact the front desk. We'll be happy to assist you personally.",
//         {
//           position: 'top-center',
//           autoClose: 5000,
//           hideProgressBar: false,
//           closeOnClick: true,
//           pauseOnHover: true,
//           draggable: true,
//           style: {
//             marginTop: '10px',
//             zIndex: 1000,
//           },
//         }
//       );
//       return;
//     }

//     setSelectedAmenities((prev) => {
//       // Remove if already exists
//       const filtered = prev.filter((a) => a.id !== currentAmenity.id);
//       // Add with new quantity if quantity > 0
//       if (amenityQuantity > 0) {
//         return [
//           ...filtered,
//           {
//             ...currentAmenity,
//             quantity: amenityQuantity,
//           },
//         ];
//       }
//       return filtered;
//     });

//     closeAmenityModal();
//   };

//   const removeAmenity = (amenityId, e) => {
//     e.stopPropagation(); // Prevent event bubbling to parent div
//     setSelectedAmenities((prev) => prev.filter((a) => a.id !== amenityId));
//   };

//   const scrollAmenities = (direction) => {
//     const amenitiesContainer = document.getElementById('amenities-container');
//     const scrollAmount = 300;
//     if (direction === 'left') {
//       amenitiesContainer.scrollLeft -= scrollAmount;
//     } else {
//       amenitiesContainer.scrollLeft += scrollAmount;
//     }
//     setAmenitiesScrollLeft(amenitiesContainer.scrollLeft);
//   };

//   const handleAutoAssign = async () => {
//     try {
//       const bookingDetails = JSON.parse(localStorage.getItem('bookingDetails'));
//       if (!bookingDetails || !bookingDetails.order_id) {
//         throw new Error('Booking details not found');
//       }

//       const response = await fetch(
//         `${BQ_BASE_URL}/bq/api/checkin-auto-assign/?order_id=${bookingDetails.order_id}`,
//         {
//           method: 'POST',
//           headers: {
//             'Content-Type': 'application/json',
//           },
//         }
//       );

//       const data = await response.json();

//       if (response.ok) {
//         // Save the assigned room details to local storage
//         localStorage.setItem('assignedRooms', JSON.stringify(data.assigned_rooms));
//         // Proceed to check-in step
//         setCurrentStep(4);
//       } else {
//         throw new Error(data.message || 'Failed to auto assign rooms');
//       }
//     } catch (error) {
//       toast.error(`Error: ${error.message}`);
//     }
//   };

//   const handleAmenitiesSubmit = async () => {
//     try {
//       const bookingDetails = JSON.parse(localStorage.getItem('bookingDetails'));
//       if (!bookingDetails || !bookingDetails.primaryBooking) {
//         throw new Error('Booking details not found');
//       }

//       const { booking_id } = bookingDetails.primaryBooking;
//       const today = new Date().toISOString();

//       // Prepare amenities data
//       const amenitiesData = {
//         bookingid: booking_id,
//         items: selectedAmenities.map((amenity) => ({
//           itemid: amenity.id,
//           quantity: amenity.quantity,
//           urgencylevel: 'Normal',
//           scheduledtime: today,
//           specialinstructions: '',
//         })),
//       };

//       const response = await fetch(`${BQ_BASE_URL}/bq/api/amenityrequests/with-billing`, {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//         },
//         body: JSON.stringify(amenitiesData),
//       });

//       const data = await response.json();

//       if (response.ok) {
//         // Refresh payment status
//         if (bookingDetails.order_id) {
//           const paymentResponse = await fetch(
//             `${BQ_BASE_URL}/bq/api/checkin-billing-details/?order_id=${bookingDetails.order_id}`
//           );
//           const paymentData = await paymentResponse.json();
//           setPaymentStatus(paymentData.payment_summary);
//         }
//         return true;
//       } else {
//         throw new Error(data.message || 'Failed to add amenities');
//       }
//     } catch (error) {
//       toast.error(`Error: ${error.message}`);
//       return false;
//     }
//   };

//   const checkPaymentStatus = async () => {
//     try {
//       const bookingDetails = JSON.parse(localStorage.getItem('bookingDetails'));
//       if (!bookingDetails || !bookingDetails.order_id) {
//         throw new Error('Booking details not found');
//       }

//       const paymentResponse = await fetch(
//         `${BQ_BASE_URL}/bq/api/checkin-billing-details/?order_id=${bookingDetails.order_id}`
//       );
//       const paymentData = await paymentResponse.json();
//       setPaymentStatus(paymentData.payment_summary);

//       return paymentData.payment_summary?.fully_paid || false;
//     } catch (error) {
//       console.error('Error checking payment status:', error);
//       return false;
//     }
//   };

//   const handleModificationSubmit = async () => {
//     try {
//       const bookingDetails = JSON.parse(localStorage.getItem('bookingDetails'));
//       if (!bookingDetails || !bookingDetails.primaryBooking) {
//         throw new Error('Booking details not found');
//       }

//       const { booking_id, checkin_date, checkout_date } = bookingDetails.primaryBooking;

//       // Prepare API URL with query parameters
//       const apiUrl = `${BQ_BASE_URL}/bq/api/modify/${booking_id}/modify?new_room_type_name=${encodeURIComponent(
//         selectedUpgrade.roomtypename
//       )}&new_checkin_date=${checkin_date}&new_checkout_date=${checkout_date}`;

//       const response = await fetch(apiUrl, {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//           Accept: 'application/json',
//         },
//         body: JSON.stringify({}), // Empty body as per curl example
//       });

//       const data = await response.json();

//       if (response.ok && data.status === 'success') {
//         // Store the modification information in local storage
//         localStorage.setItem(
//           'roomModification',
//           JSON.stringify({
//             newRoomType: selectedUpgrade.roomtypename,
//             effectiveDate: new Date().toISOString().split('T')[0],
//             modificationType: 'upgrade',
//             priceDifference: data.data.price_difference,
//             netPriceImpact: data.data.net_price_impact,
//             oldTotal: data.data.old_total,
//             newTotal: data.data.new_total,
//             financialAction: data.data.financial_action,
//             policyStatus: data.data.policy.modification_status,
//             isRestricted: data.data.redzone.is_restricted,
//             redzoneStatus: data.data.redzone.redzone_status,
//             // Store the full response for reference
//             fullResponse: data,
//           })
//         );

//         // Also update the booking details in local storage with new room type
//         const updatedBookingDetails = {
//           ...bookingDetails,
//           primaryBooking: {
//             ...bookingDetails.primaryBooking,
//             room_type: {
//               ...bookingDetails.primaryBooking.room_type,
//               name: selectedUpgrade.roomtypename,
//             },
//             payment_status: {
//               ...bookingDetails.primaryBooking.payment_status,
//               total_amount: data.data.new_total,
//             },
//           },
//         };
//         localStorage.setItem('bookingDetails', JSON.stringify(updatedBookingDetails));

//         // Refresh payment status if order_id exists
//         if (bookingDetails.order_id) {
//           const paymentResponse = await fetch(
//             `${BQ_BASE_URL}/bq/api/checkin-billing-details/?order_id=${bookingDetails.order_id}`
//           );
//           const paymentData = await paymentResponse.json();
//           setPaymentStatus(paymentData.payment_summary);
//         }

//         toast.success('Room upgrade applied successfully!', {
//           position: 'top-center',
//           autoClose: 3000,
//         });

//         return true;
//       } else {
//         throw new Error(data.message || 'Failed to apply room modification');
//       }
//     } catch (error) {
//       toast.error(`Error: ${error.message}`, {
//         position: 'top-center',
//         autoClose: 5000,
//       });
//       return false;
//     }
//   };

//   const showUpgradeConfirmationModal = () => {
//     if (!selectedUpgrade) return false;

//     // Calculate upgrade details
//     const upgradePrice = selectedUpgrade.baseprice * lengthOfStay;
//     const totalAmenitiesPrice = selectedAmenities.reduce(
//       (total, amenity) => total + amenity.baseprice * amenity.quantity,
//       0
//     );
//     const totalAdditionalCost = upgradePrice + totalAmenitiesPrice;

//     setUpgradeConfirmationData({
//       currentRoom: currentRoomType,
//       currentPrice: currentRoomPrice,
//       newRoom: selectedUpgrade.roomtypename,
//       upgradePrice: upgradePrice,
//       amenitiesPrice: totalAmenitiesPrice,
//       totalAdditionalCost: totalAdditionalCost,
//       nights: lengthOfStay,
//       perNightPrice: selectedUpgrade.baseprice,
//       images: selectedUpgrade.images,
//     });

//     setShowUpgradeConfirmation(true);
//     return true;
//   };

//   const confirmUpgrade = async () => {
//     setShowUpgradeConfirmation(false);
//     await proceedWithUpgrade();
//   };

//   const cancelUpgrade = () => {
//     setShowUpgradeConfirmation(false);
//   };

//   const proceedWithUpgrade = async () => {
//     try {
//       let proceed = true;
//       let hasModification = false;

//       // Process amenities if any selected
//       if (selectedAmenities.length > 0) {
//         proceed = await handleAmenitiesSubmit();
//       }

//       // Process room modification if selected
//       if (proceed && selectedUpgrade) {
//         hasModification = true;
//         proceed = await handleModificationSubmit();

//         if (proceed) {
//           // Check if modification was restricted or has charges
//           const modificationData = JSON.parse(localStorage.getItem('roomModification'));
//         }
//       }

//       // Check payment status after any updates
//       const isFullyPaid = await checkPaymentStatus();

//       // Determine next step based on payment status
//       if (proceed) {
//         if (isFullyPaid) {
//           // If fully paid and no modification or modification is paid, auto assign
//           if (!hasModification || (hasModification && paymentStatus?.fully_paid)) {
//             await handleAutoAssign(); // This will go to step 4 if fully paid
//           } else {
//             // If modification created additional charges, go to payment
//             setCurrentStep(3);
//           }
//         } else {
//           setCurrentStep(3); // Payment step
//         }
//       }
//     } catch (error) {
//       toast.error(`Error: ${error.message}`);
//     }
//   };

//   const handleContinue = async () => {
//     try {
//       // If user selected a room upgrade, show confirmation modal
//       if (selectedUpgrade) {
//         showUpgradeConfirmationModal();
//       } else {
//         // If no upgrade selected, proceed normally
//         await proceedWithUpgrade();
//       }
//     } catch (error) {
//       toast.error(`Error: ${error.message}`);
//     }
//   };

//   const handleSkip = async () => {
//     try {
//       // First check payment status
//       const isFullyPaid = await checkPaymentStatus();

//       if (isFullyPaid) {
//         // If fully paid, auto assign rooms and go to check-in
//         await handleAutoAssign();
//       } else {
//         // If not fully paid, go to payment step without auto assigning
//         setCurrentStep(3);
//       }
//     } catch (error) {
//       toast.error(`Error: ${error.message}`);
//     }
//   };

//   if (loading) {
//     return <div className="text-center py-8">Loading enhancements...</div>;
//   }

//   return (
//     <div className="">
//       <div className="mb-6">
//         <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-4 gap-3">
//           <div>
//             <h2 className="text-xl font-bold text-gray-900">Enhance Your Stay</h2>
//             <p className="text-gray-600 text-sm mt-1">
//               Enhance your reservation with room upgrades and relaxing amenities
//             </p>
//           </div>

//           {currentRoomType && (
//             <div className="text-left sm:text-right">
//               <h3 className="font-semibold text-sm text-gray-600">Your Current Room</h3>
//               <div className="flex flex-wrap sm:flex-nowrap items-center justify-start sm:justify-end gap-2">
//                 <h4 className="font-bold text-lg">{currentRoomType}</h4>
//                 <span className="font-bold text-lg">-</span>
//                 <p className="font-bold text-lg">₹{currentRoomPrice.toLocaleString()}</p>
//               </div>
//             </div>
//           )}
//         </div>
//       </div>

//       {/* Room Modification Options Section */}
//       <div className="mb-8">
//         <h3 className="font-semibold text-lg mb-3">Room Upgrade Options</h3>
//         {modificationOptions?.available_modifications?.length > 0 ? (
//           <div className="grid lg:grid-cols-3 gap-4 md:grid-cols-2">
//             {modificationOptions.available_modifications.map((upgrade) => (
//               <div
//                 key={upgrade.roomtypename}
//                 className={`border rounded-lg p-0 transition-all flex h-full ${
//                   selectedUpgrade?.roomtypename === upgrade.roomtypename
//                     ? 'border-blue-500 bg-blue-50'
//                     : 'border-gray-200 hover:border-gray-300'
//                 }`}
//               >
//                 {/* Image on left - full height */}
//                 <div className="w-2/4 h-full overflow-hidden rounded-l-lg relative">
//                   <div className="absolute inset-0">
//                     <img
//                       src={upgrade.images[0]}
//                       alt={upgrade.roomtypename}
//                       className="w-full h-full object-cover"
//                     />
//                     {upgrade.images.length > 1 && (
//                       <>
//                         <div className="absolute top-0 left-0 w-full h-full flex items-center justify-between px-2">
//                           <button
//                             className="bg-black bg-opacity-30 text-white p-1 rounded-full hover:bg-opacity-50 z-10"
//                             onClick={(e) => {
//                               e.stopPropagation();
//                               const newIndex =
//                                 currentImageIndex === 0
//                                   ? upgrade.images.length - 1
//                                   : currentImageIndex - 1;
//                               setCurrentImageIndex(newIndex);
//                             }}
//                           >
//                             <FaChevronLeft />
//                           </button>
//                           <button
//                             className="bg-black bg-opacity-30 text-white p-1 rounded-full hover:bg-opacity-50 z-10"
//                             onClick={(e) => {
//                               e.stopPropagation();
//                               const newIndex =
//                                 currentImageIndex === upgrade.images.length - 1
//                                   ? 0
//                                   : currentImageIndex + 1;
//                               setCurrentImageIndex(newIndex);
//                             }}
//                           >
//                             <FaChevronRight />
//                           </button>
//                         </div>
//                         <div className="absolute bottom-2 left-0 w-full flex justify-center space-x-1">
//                           {upgrade.images.map((_, index) => (
//                             <div
//                               key={index}
//                               className={`w-2 h-2 rounded-full ${
//                                 currentImageIndex === index ? 'bg-white' : 'bg-white bg-opacity-50'
//                               }`}
//                             />
//                           ))}
//                         </div>
//                       </>
//                     )}
//                     <button
//                       onClick={(e) => openRoomImagesModal(upgrade.images, e)}
//                       className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white p-1 rounded hover:bg-opacity-70"
//                     >
//                       <FaExpand size={12} />
//                     </button>
//                   </div>
//                 </div>

//                 {/* Content on right */}
//                 <div className="w-1/2 flex flex-col p-4 ">
//                   <div>
//                     <h4 className="font-bold text-lg mb-2">{upgrade.roomtypename}</h4>
//                     <p className="text-black  text-xs">
//                       ₹{upgrade.baseprice.toLocaleString()}
//                       <span className="text-black text-xs ml-1">/ Per night</span>
//                     </p>
//                     {/* Add this line below the per night price */}
//                     <p className="text-black font-bold text-lg">
//                       ₹{(upgrade.baseprice * lengthOfStay).toLocaleString()}
//                       <span className="text-black font-bold ml-1">
//                         / {lengthOfStay} Night{lengthOfStay !== 1 ? 's' : ''}
//                       </span>
//                     </p>

//                     {/* Display available rooms count */}
//                     <ul className="text-xs text-gray-500 space-y-1 mt-2">
//                       <li className="flex items-start">
//                         <span className="inline-block w-1 h-1 mt-1.5 mr-1.5 bg-gray-400 rounded-full flex-shrink-0"></span>
//                         <span>Excluding taxes and fees</span>
//                       </li>
//                       <li className="flex items-start">
//                         <span className="inline-block w-1 h-1 mt-1.5 mr-1.5 bg-gray-400 rounded-full flex-shrink-0"></span>
//                         <span>Available immediately</span>
//                       </li>
//                       <li className="flex items-start">
//                         <span className="inline-block w-1 h-1 mt-1.5 mr-1.5 bg-gray-400 rounded-full flex-shrink-0"></span>
//                         <span>
//                           {roomAvailability[upgrade.roomtypename] !== undefined && (
//                             <div
//                               className={`text-xs mb-2 ${
//                                 roomAvailability[upgrade.roomtypename] > 5
//                                   ? 'text-green-600'
//                                   : 'text-red-600'
//                               }`}
//                             >
//                               {roomAvailability[upgrade.roomtypename] > 0
//                                 ? `Only ${roomAvailability[upgrade.roomtypename]} room${
//                                     roomAvailability[upgrade.roomtypename] !== 1 ? 's' : ''
//                                   } available`
//                                 : 'Sold out'}
//                             </div>
//                           )}
//                         </span>
//                       </li>
//                     </ul>
//                   </div>

//                   {/* Button at bottom */}
//                   <div className="mt-auto pt-4 flex justify-end">
//                     {selectedUpgrade?.roomtypename === upgrade.roomtypename ? (
//                       <button
//                         onClick={(e) => {
//                           e.stopPropagation();
//                           handleRemoveUpgrade(e);
//                         }}
//                         className="w-1/2 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded-md transition font-medium"
//                       >
//                         Remove
//                       </button>
//                     ) : (
//                       <button
//                         onClick={(e) => {
//                           e.stopPropagation();
//                           handleUpgradeSelect(upgrade);
//                         }}
//                         disabled={roomAvailability[upgrade.roomtypename] === 0}
//                         className={`w-1/2 py-1.5 bg-black hover:bg-gray-800 text-white rounded-md transition font-medium ${
//                           roomAvailability[upgrade.roomtypename] === 0
//                             ? 'opacity-50 cursor-not-allowed'
//                             : ''
//                         }`}
//                       >
//                         {roomAvailability[upgrade.roomtypename] === 0 ? 'Sold Out' : 'Select'}
//                       </button>
//                     )}
//                   </div>
//                 </div>
//               </div>
//             ))}
//           </div>
//         ) : (
//           <div className="text-gray-600 py-1 text-sm">
//             No room upgrades available, No further upgrades are available as you've already selected
//             the top-tier room option
//           </div>
//         )}
//       </div>

//       {/* Amenities Section */}
//       <div className="mb-8 relative">
//         <h3 className="font-semibold text-lg mb-3">Additional Amenities</h3>
//         <div className="flex items-center justify-between mb-4">
//           <span className="text-sm text-gray-600">
//             {selectedAmenities.length > 0
//               ? `${selectedAmenities.reduce(
//                   (total, amenity) => total + amenity.quantity,
//                   0
//                 )} items selected`
//               : 'No amenities selected'}
//           </span>
//           {selectedAmenities.length > 0 && (
//             <div className="flex items-center gap-2">
//               <span className="text-sm font-semibold bg-gray-100 px-3 py-1 rounded-full">
//                 Total: ₹
//                 {selectedAmenities
//                   .reduce((total, amenity) => total + amenity.baseprice * amenity.quantity, 0)
//                   .toLocaleString()}
//               </span>
//             </div>
//           )}
//         </div>
//         <div className="relative">
//           {amenitiesScrollLeft > 0 && (
//             <button
//               onClick={() => scrollAmenities('left')}
//               className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-white rounded-full p-2 shadow-md hover:bg-gray-100"
//               style={{ marginLeft: '-16px' }}
//             >
//               <FaChevronLeft className="text-gray-600" />
//             </button>
//           )}
//           <div
//             id="amenities-container"
//             className="flex overflow-x-auto space-x-4 pb-4 scroll-smooth"
//             style={{ scrollbarWidth: 'none' }}
//           >
//             {amenities.map((amenity) => {
//               const isSelected = selectedAmenities.some((a) => a.id === amenity.id);
//               const selectedItem = isSelected
//                 ? selectedAmenities.find((a) => a.id === amenity.id)
//                 : null;
//               const totalPrice = isSelected ? amenity.baseprice * selectedItem.quantity : 0;

//               return (
//                 <div
//                   key={amenity.id}
//                   className={`flex-shrink-0 border rounded-lg p-0 cursor-pointer transition-all flex ${
//                     isSelected ? 'h-42 w-96' : 'h-44 w-80'
//                   } ${
//                     isSelected
//                       ? 'border-blue-300 bg-blue-50 shadow-md'
//                       : 'border-gray-200 hover:border-gray-300'
//                   }`}
//                   onClick={() => openAmenityModal(amenity)}
//                 >
//                   {/* Image on left */}
//                   <div
//                     className={`${
//                       isSelected ? 'w-2/5' : 'w-2/3'
//                     } h-full overflow-hidden rounded-l-lg relative`}
//                   >
//                     <img
//                       src={amenity.imageurl}
//                       alt={amenity.name}
//                       className="w-full h-full object-cover"
//                     />
//                     <div
//                       className={`absolute top-2 right-2 text-xs px-2 py-1 rounded-full ${
//                         amenity.refundable
//                           ? 'bg-green-100 text-green-800'
//                           : 'bg-red-100 text-red-800'
//                       }`}
//                     >
//                       {amenity.refundable ? 'Refundable' : 'Non-refundable'}
//                     </div>
//                   </div>

//                   {/* Content on right */}
//                   <div
//                     className={`${
//                       isSelected ? 'w-3/5' : 'w-2/3'
//                     } p-3 flex flex-col justify-between`}
//                   >
//                     <div>
//                       <h4 className="font-medium text-sm line-clamp-1">{amenity.name}</h4>
//                       <div className="flex items-baseline mt-1">
//                         <span className="text-black font-semibold text-sm">
//                           ₹{amenity.baseprice?.toLocaleString()}
//                           <span className="text-gray-500 text-xs lowercase ml-1">
//                             /{amenity.price_type}
//                           </span>
//                         </span>
//                       </div>
//                     </div>

//                     {isSelected ? (
//                       <div className="flex flex-col">
//                         <div className="flex justify-between items-center text-xs mb-1">
//                           <span className="text-gray-600">Quantity:</span>
//                           <div className="flex items-center gap-2">
//                             <span className="font-medium">{selectedItem.quantity}</span>
//                           </div>
//                         </div>
//                         <div className="flex justify-between items-center">
//                           <span className="text-gray-600 text-xs">Total:</span>
//                           <span className="text-black font-semibold text-sm">
//                             ₹{totalPrice.toLocaleString()}
//                           </span>
//                         </div>
//                         <button
//                           onClick={(e) => {
//                             e.stopPropagation();
//                             removeAmenity(amenity.id, e);
//                           }}
//                           className="mt-2 py-1.5 px-3 rounded text-xs font-medium transition self-end bg-red-500 hover:bg-red-600 text-white"
//                         >
//                           Remove
//                         </button>
//                       </div>
//                     ) : (
//                       <button
//                         onClick={(e) => {
//                           e.stopPropagation();
//                           openAmenityModal(amenity);
//                         }}
//                         className="py-1.5 px-6 rounded text-xs font-medium transition self-end bg-black hover:bg-gray-800 text-white"
//                       >
//                         Add
//                       </button>
//                     )}
//                   </div>
//                 </div>
//               );
//             })}
//           </div>
//           <button
//             onClick={() => scrollAmenities('right')}
//             className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-white rounded-full p-2 shadow-md hover:bg-gray-100"
//             style={{ marginRight: '-16px' }}
//           >
//             <FaChevronRight className="text-gray-600" />
//           </button>
//         </div>
//       </div>

//       {/* Amenity Quantity Modal */}
//       {showAmenityModal && currentAmenity && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//           <ToastContainer
//             position="top-center"
//             autoClose={5000}
//             hideProgressBar={false}
//             newestOnTop={false}
//             closeOnClick
//             rtl={false}
//             pauseOnFocusLoss
//             draggable
//             pauseOnHover
//           />
//           <div className="bg-white rounded-lg p-6 w-full max-w-lg">
//             <div className="flex justify-between items-center mb-4">
//               <h3 className="text-xl font-bold">{currentAmenity.name}</h3>
//               <button onClick={closeAmenityModal} className="text-gray-500 hover:text-gray-700">
//                 <FaTimes />
//               </button>
//             </div>

//             <p className="text-gray-600 text-sm mb-6">
//               Select the number of people for whom you'd like to include this service.
//             </p>

//             <div className="mb-6">
//               <div className="flex items-center justify-between mb-2">
//                 <span className="font-medium">Quantity:</span>
//                 <div className="flex items-center gap-2">
//                   <button
//                     onClick={() => decrementQuantity(currentAmenity)}
//                     className={`w-8 h-8 flex items-center justify-center border rounded-full hover:bg-gray-100 ${
//                       amenityQuantity <= 1 ? 'text-gray-400 cursor-not-allowed' : ''
//                     }`}
//                     disabled={amenityQuantity <= 1}
//                   >
//                     <FaMinus />
//                   </button>
//                   <span className="mx-4 text-lg font-medium">{amenityQuantity}</span>
//                   <button
//                     onClick={() => incrementQuantity(currentAmenity)}
//                     className={`w-8 h-8 flex items-center justify-center border rounded-full hover:bg-gray-100 ${
//                       amenityQuantity >= currentAmenity.max_quantity
//                         ? 'text-gray-400 cursor-not-allowed'
//                         : ''
//                     }`}
//                   >
//                     <FaPlus />
//                   </button>
//                 </div>
//               </div>

//               {/* Show message when max quantity reached */}
//             </div>

//             <div className="space-y-2 mb-6">
//               <div className="flex justify-between">
//                 <span className="text-gray-600">Price per item:</span>
//                 <span className="font-medium">
//                   ₹{currentAmenity.baseprice}
//                   <span className="text-gray-500 text-xs text-transform: lowercase ">
//                     /{currentAmenity.price_type}
//                   </span>
//                 </span>
//               </div>
//               <div className="flex justify-between">
//                 <span className="text-gray-600">Total price:</span>
//                 <span className="text-black font-semibold">
//                   ₹{(currentAmenity.baseprice * amenityQuantity).toLocaleString()}{' '}
//                 </span>
//               </div>
//               <div className="flex justify-between">
//                 <span className="text-gray-600 text-sm">
//                   {currentAmenity.value === 0 ? 'Tax not applicable' : 'Excluding taxes and fees'}
//                 </span>
//               </div>
//             </div>

//             <div className="flex justify-end space-x-4 pt-4 border-t">
//               <button
//                 onClick={closeAmenityModal}
//                 className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={saveAmenitySelection}
//                 className="px-6 py-2 bg-black text-white rounded-md hover:text-gray-300"
//               >
//                 {selectedAmenities.some((a) => a.id === currentAmenity.id) ? 'Update' : 'Add'}
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Room Images Modal */}
//       {showRoomImagesModal && (
//         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
//           <div className="relative w-full max-w-3xl max-h-[60vh] bg-white rounded-xl shadow-xl flex flex-col">
//             <div className="flex justify-between items-center p-4 border-b">
//               <h1 className="text-lg font-semibold">View Room Images</h1>
//               <button
//                 onClick={closeRoomImagesModal}
//                 className="text-gray-500 hover:text-gray-700 transition text-2xl"
//               >
//                 <FaTimes />
//               </button>
//             </div>

//             <div className="relative flex-1 flex items-center justify-center p-4 overflow-hidden">
//               <img
//                 src={currentRoomImages[currentImageIndex]}
//                 alt={`Room ${currentImageIndex + 1}`}
//                 className="max-w-full max-h-[calc(60vh-120px)] object-contain"
//               />

//               <button
//                 className="absolute left-2 md:left-4 top-1/2 transform -translate-y-1/2 bg-gray-800 bg-opacity-60 text-white p-2 rounded-full hover:bg-opacity-80 transition z-50"
//                 onClick={handlePrevImage}
//               >
//                 <FaChevronLeft size={20} />
//               </button>
//               <button
//                 className="absolute right-2 md:right-4 top-1/2 transform -translate-y-1/2 bg-gray-800 bg-opacity-60 text-white p-2 rounded-full hover:bg-opacity-80 transition z-50"
//                 onClick={handleNextImage}
//               >
//                 <FaChevronRight size={20} />
//               </button>
//             </div>

//             <div className="p-4 flex justify-center">
//               <div className="flex space-x-2">
//                 {currentRoomImages.map((_, index) => (
//                   <button
//                     key={index}
//                     className={`w-2 h-2 md:w-3 md:h-3 rounded-full ${
//                       currentImageIndex === index ? 'bg-gray-800' : 'bg-gray-300'
//                     }`}
//                     onClick={() => setCurrentImageIndex(index)}
//                     aria-label={`Go to image ${index + 1}`}
//                   />
//                 ))}
//               </div>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Room Upgrade Confirmation Modal */}
//       {showUpgradeConfirmation && upgradeConfirmationData && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//           <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden">
//             {/* Header with warning icon */}
//             <div className="bg-yellow-50 border-b border-yellow-200 p-6">
//               <div className="flex items-center gap-4">
//                 <div className="flex-shrink-0">
//                   <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
//                     <FaExclamationTriangle className="text-yellow-600 text-xl" />
//                   </div>
//                 </div>
//                 <div>
//                   <h2 className="text-2xl font-bold text-gray-900">Confirm Room Upgrade</h2>
//                   <p className="text-gray-600 mt-1">
//                     This action cannot be undone. Please review your upgrade details below.
//                   </p>
//                 </div>
//               </div>
//             </div>

//             {/* Content */}
//             <div className="p-6 max-h-[60vh] overflow-y-auto">
//               {/* Current vs New Room Comparison */}
//               <div className="grid md:grid-cols-2 gap-6 mb-6">
//                 {/* Current Room */}
//                 <div className="border border-gray-200 rounded-lg p-4">
//                   <h3 className="text-sm font-medium text-gray-500 mb-2">Current Room</h3>
//                   <h4 className="text-lg font-bold text-gray-900 mb-2">
//                     {upgradeConfirmationData.currentRoom}
//                   </h4>

//                   <div className="mt-4">
//                     <div className="flex items-center text-sm text-gray-600">
//                       <span className="w-2 h-2 bg-gray-400 rounded-full mr-2"></span>
//                       Your current selection
//                     </div>
//                   </div>
//                 </div>

//                 {/* New Room */}
//                 <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
//                   <h3 className="text-sm font-medium text-gray-500 mb-2">Upgrading To</h3>
//                   <h4 className="text-lg font-bold text-gray-900 mb-2">
//                     {upgradeConfirmationData.newRoom}
//                   </h4>
//                   <div className="space-y-1">
//                     <p className="text-sm text-gray-600">
//                       ₹{upgradeConfirmationData.perNightPrice.toLocaleString()} / night
//                     </p>
//                     <p className="text-2xl font-bold text-blue-700">
//                       ₹{upgradeConfirmationData.upgradePrice.toLocaleString()}
//                       <span className="text-sm font-normal text-gray-600 ml-2">
//                         for {upgradeConfirmationData.nights} night
//                         {upgradeConfirmationData.nights !== 1 ? 's' : ''}
//                       </span>
//                     </p>
//                   </div>
//                   {upgradeConfirmationData.images && upgradeConfirmationData.images.length > 0 && (
//                     <div className="mt-4">
//                       <img
//                         src={upgradeConfirmationData.images[0]}
//                         alt={upgradeConfirmationData.newRoom}
//                         className="w-full h-32 object-cover rounded-md"
//                       />
//                     </div>
//                   )}
//                 </div>
//               </div>

//               {/* Additional Costs */}
//               <div className="border border-gray-200 rounded-lg p-4 mb-6">
//                 <h3 className="font-medium text-gray-900 mb-3">Additional Costs</h3>
//                 <div className="space-y-2">
//                   <div className="flex justify-between">
//                     <span className="text-gray-600">
//                       Room Upgrade ({upgradeConfirmationData.nights} night
//                       {upgradeConfirmationData.nights !== 1 ? 's' : ''}):
//                     </span>
//                     <span className="font-medium">
//                       ₹{upgradeConfirmationData.upgradePrice.toLocaleString()}
//                     </span>
//                   </div>
//                   {upgradeConfirmationData.amenitiesPrice > 0 && (
//                     <div className="flex justify-between">
//                       <span className="text-gray-600">Amenities:</span>
//                       <span className="font-medium">
//                         ₹{upgradeConfirmationData.amenitiesPrice.toLocaleString()}
//                       </span>
//                     </div>
//                   )}
//                   <div className="border-t pt-2 mt-2">
//                     <div className="flex justify-between">
//                       <span className="font-bold text-gray-900">Total Additional Cost:</span>
//                       <span className="font-bold text-lg text-blue-700">
//                         ₹{upgradeConfirmationData.totalAdditionalCost.toLocaleString()}
//                       </span>
//                     </div>
//                   </div>
//                 </div>
//               </div>

//               {/* Important Notes */}
//               <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
//                 <h4 className="font-medium text-yellow-800 mb-2">Important Notes:</h4>
//                 <ul className="text-sm text-yellow-700 space-y-1">
//                   <li className="flex items-start">
//                     <span className="inline-block w-1 h-1 mt-1.5 mr-2 bg-yellow-500 rounded-full flex-shrink-0"></span>
//                     This upgrade is <span className="font-bold">irreversible</span> and cannot be
//                     undone
//                   </li>
//                   <li className="flex items-start">
//                     <span className="inline-block w-1 h-1 mt-1.5 mr-2 bg-yellow-500 rounded-full flex-shrink-0"></span>
//                     The new room will be assigned immediately after confirmation
//                   </li>
//                   <li className="flex items-start">
//                     <span className="inline-block w-1 h-1 mt-1.5 mr-2 bg-yellow-500 rounded-full flex-shrink-0"></span>
//                     You may need to pay any additional charges before check-in
//                   </li>
//                   <li className="flex items-start">
//                     <span className="inline-block w-1 h-1 mt-1.5 mr-2 bg-yellow-500 rounded-full flex-shrink-0"></span>
//                     All prices are exclusive of taxes and fees
//                   </li>
//                 </ul>
//               </div>
//             </div>

//             {/* Action Buttons */}
//             <div className="border-t border-gray-200 p-6">
//               <div className="flex flex-col sm:flex-row justify-end gap-3">
//                 <button
//                   onClick={cancelUpgrade}
//                   className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition"
//                 >
//                   Cancel
//                 </button>
//                 <button
//                   onClick={confirmUpgrade}
//                   className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 font-medium transition flex items-center justify-center"
//                 >
//                   Yes, Upgrade My Room
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}

//       <div className="flex flex-col md:flex-row justify-between gap-4">
//         <button
//           onClick={() => setCurrentStep(currentStep - 1)}
//           className="w-full md:w-1/6 bg-gray-200 text-gray-800 py-2 rounded hover:bg-gray-300 transition font-medium"
//         >
//           Back
//         </button>

//         <div className="flex flex-col md:flex-row gap-4 w-full md:w-auto">
//           <button
//             onClick={handleSkip}
//             className="w-full md:w-[250px] bg-gray-200 text-gray-800 py-2 rounded hover:bg-gray-300 transition font-medium"
//           >
//             Skip
//           </button>
//           <button
//             onClick={handleContinue}
//             className="w-full md:w-[250px] bg-black text-white py-2 rounded hover:bg-gray-800 transition font-medium"
//           >
//             {selectedUpgrade || selectedAmenities.length > 0 ? 'Confirm Purchase' : 'Next'}
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default EnhanceStay;
